import { ContractStatus } from "@boark/shared/types/contract";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  ContractForm,
  ContractFormValues,
} from "../../components/contract-form";
import { Modal } from "../../components/modal";
import { useGetContract } from "../../queries/contract";
import {
  contractFormValuesToUpdate,
  updateContract,
} from "../../services/contract";
import { formatDate } from "../../utils/date";
import { navigateBack } from "../../utils/navigate-back";

export function ContractEdit() {
  const navigate = useNavigate();
  const { id } = useParams();

  if (!id) {
    throw new Error("Contract ID is required");
  }

  const { data: contract, isLoading: isLoadingContract } = useGetContract(id);
  const queryClient = useQueryClient();

  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const [formValues, setFormValues] = useState<ContractFormValues>({
    title: "",
    description: "",
    tags: [],
    status: ContractStatus.ACTIVE,
    cost: "",
    invoiceType: "",
    fromDate: "",
    toDate: "",
    counterPartyId: "",
  });

  const isLoading = isLoadingContract || loading;

  useEffect(() => {
    if (contract) {
      setFormValues({
        title: contract.title,
        description: contract.description ?? "",
        tags: [...contract.tags],
        status: contract.status,
        cost: contract.cost?.toString() ?? "",
        invoiceType: contract.invoiceType ?? "",
        fromDate: formatDate(contract.fromDate),
        toDate: contract.toDate ? formatDate(contract.toDate) : "",
        counterPartyId: contract.counterParty?.id ?? "",
      });
    }
  }, [contract]);

  const closeModal = () => {
    navigateBack(navigate, "/admin/contract");
  };

  return (
    <Modal open onClose={() => closeModal()}>
      <ContractForm
        hasError={hasError}
        loading={isLoading}
        values={formValues}
        onCancel={() => closeModal()}
        onChange={setFormValues}
        onSubmit={() => {
          (async () => {
            setLoading(true);
            try {
              await updateContract(id, contractFormValuesToUpdate(formValues));
              await Promise.all([
                queryClient.invalidateQueries({ queryKey: ["contract", id] }),
                queryClient.invalidateQueries({ queryKey: ["contracts"] }),
              ]);
              closeModal();
            } catch {
              setHasError(true);
            } finally {
              setLoading(false);
            }
          })();
        }}
      />
    </Modal>
  );
}
